import { useEffect } from 'react';
import { ActionType, ArticlePath, createGuideLink, RouteName, TextKey } from '@mews-commander/enums';
import { WithAccountId } from '@mews-commander/types';
import { AccountType, useAppConfiguration } from '@mews-commander/data-access';
import { IconName, Spinner } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { ResolveRoute, useResolveRoute } from '@mews/routing';
import { getFullName, getImageUrlOrNull, useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { isCurrentEnterpriseResDiaryIntegrationEnabled } from '@mews-commander/legacy-entities/src/selectors';
import { getCustomer, getCustomerOrCompanyFullName } from '@mews-commander/legacy-entities/src/ducks/customers';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { useCloseButton } from '../../../core/windowManager/hooks';
import { changeDocumentTitle } from '../../../middleware';
import { useTabsConfig } from '../../modalManager/tabs';
import { useGetAccount } from '../../accounts';

interface GetTabsProps {
    resolveRoute: ResolveRoute;
    accountId: string;
    enterpriseId: string;
    isResDiaryEnabled: boolean;
}

const getTabs = ({ resolveRoute, accountId, enterpriseId, isResDiaryEnabled }: GetTabsProps) => [
    {
        key: TextKey.Dashboard,
        url: resolveRoute(RouteName.CustomerDashboard, { id: accountId, query: { EnterpriseId: enterpriseId } }),
    },
    {
        key: TextKey.Profile,
        url: resolveRoute(RouteName.CustomerDetail, { id: accountId, query: { EnterpriseId: enterpriseId } }),
    },
    {
        key: TextKey.InternalInformation,
        url: resolveRoute(RouteName.CustomerInternals, { id: accountId, query: { EnterpriseId: enterpriseId } }),
    },
    {
        key: TextKey.PaymentPlural,
        url: resolveRoute(RouteName.PaymentIndex, { accountId, query: { EnterpriseId: enterpriseId } }),
    },
    {
        key: TextKey.Billing,
        url: resolveRoute(RouteName.BillingAccount, { accountId, query: { EnterpriseId: enterpriseId } }),
    },
    {
        key: TextKey.CustomerActionLog,
        url: resolveRoute(RouteName.CustomerActionLog, { id: accountId, query: { EnterpriseId: enterpriseId } }),
    },
    ...(isResDiaryEnabled
        ? [
              {
                  key: TextKey.TableBookings,
                  url: resolveRoute(RouteName.CustomerTableBookings, {
                      id: accountId,
                      query: { EnterpriseId: enterpriseId },
                  }),
              },
          ]
        : []),
];

export const CustomerDetailModalHeader = ({ accountId, activeTab }: { activeTab: Mews.TextKey } & WithAccountId) => {
    const dispatch = useCommanderThunkDispatch();
    const enterpriseId = useCurrentEnterpriseId();

    const { imageBaseUrl } = useAppConfiguration();

    // when navigating from other tabs it skips fetching the account data when they are already loaded into the store and makes the navigation more seamless
    // TODO: remove when all tabs are using this component
    const accountNameFromStore = useCommanderSelector((state) =>
        getCustomerOrCompanyFullName(state, { id: accountId })
    );
    const customerFromStore = useCommanderSelector((state) => getCustomer(state, { id: accountId }));
    const { data: account } = useGetAccount({ accountId }, { enabled: !(accountNameFromStore && customerFromStore) });
    const accountName =
        account?.data.discriminator === AccountType.Customer ? getFullName(account?.data.value) : accountNameFromStore;
    const classifications =
        account?.data.discriminator === AccountType.Customer
            ? account?.data.value.classifications
            : customerFromStore?.classifications;
    const imageUrl = getImageUrlOrNull(imageBaseUrl)({
        imageId:
            account?.data.discriminator === AccountType.Customer
                ? account?.data.value.imageId
                : customerFromStore?.imageId,
    });

    const closeButton = useCloseButton();
    const resolveRoute = useResolveRoute();
    const isResDiaryEnabled = useCommanderSelector((state) => isCurrentEnterpriseResDiaryIntegrationEnabled(state, {}));

    useEffect(() => {
        if (accountName) {
            dispatch(changeDocumentTitle(accountName));
        }
    }, [accountName, dispatch]);

    const tabsConfig = useTabsConfig({
        tabs: getTabs({ resolveRoute, accountId, enterpriseId, isResDiaryEnabled }),
        activeTab,
    });

    // this component is currently used only in the Payments and Billing tabs which have the same actions
    const actions: HeadingAction[] = [
        {
            url: createGuideLink(ArticlePath.TakePayment),
            target: '_blank',
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
        },
    ];

    return (
        <ModalHeader
            title={accountName ?? <Spinner />}
            imageUrl={imageUrl}
            classifications={classifications}
            tabsConfig={tabsConfig}
            primaryAction={closeButton}
            actions={actions}
        />
    );
};
